import React, { useEffect, useState } from 'react';
import { Row, Col } from 'antd';
import { Link } from 'react-router-dom';
import {
    HomeOutlined,
    AppleOutlined,
    ThunderboltOutlined,
    SwapOutlined,
    SettingOutlined,
    CarOutlined,
    LinkOutlined,
    DisconnectOutlined,
    PhoneOutlined,
    CustomerServiceOutlined,
    ShoppingCartOutlined,
    DeliveredProcedureOutlined,
    ShopOutlined,
    CheckCircleFilled,
    RightOutlined,
    DownloadOutlined,
    UserAddOutlined,
    CheckCircleOutlined,
    ArrowRightOutlined
} from '@ant-design/icons';
import { useScrollAnimation } from '../../common/hooks';
import { FEATURES, GOOGLE_PLAY_URL, APP_STORE_URL, APP_STORE_AVAILABLE } from '../../common/constants';
import '../../styles/service-showcase.css';
import './FeaturesPage.css';

// Icon mapping
const iconMap = {
    'shopping-cart': ShoppingCartOutlined,
    'delivered-procedure': DeliveredProcedureOutlined,
    'shop': ShopOutlined,
    'thunderbolt': ThunderboltOutlined,
    'swap': SwapOutlined,
    'setting': SettingOutlined,
    'car': CarOutlined,
    'link': LinkOutlined,
    'disconnect': DisconnectOutlined,
    'phone': PhoneOutlined,
    'customer-service': CustomerServiceOutlined,
};

// Color gradients for icons
const colorGradients = {
    '#f5222d': 'linear-gradient(135deg, #f5222d 0%, #ff7a45 100%)',
    '#7cb305': 'linear-gradient(135deg, #7cb305 0%, #bae637 100%)',
    '#1d39c4': 'linear-gradient(135deg, #1d39c4 0%, #597ef7 100%)',
    '#fa8c16': 'linear-gradient(135deg, #fa8c16 0%, #ffc53d 100%)',
    '#52c41a': 'linear-gradient(135deg, #52c41a 0%, #95de64 100%)',
    '#1890ff': 'linear-gradient(135deg, #1890ff 0%, #69c0ff 100%)',
    '#13c2c2': 'linear-gradient(135deg, #13c2c2 0%, #5cdbd3 100%)',
    '#722ed1': 'linear-gradient(135deg, #722ed1 0%, #b37feb 100%)',
    '#eb2f96': 'linear-gradient(135deg, #eb2f96 0%, #ff85c0 100%)',
    '#2f54eb': 'linear-gradient(135deg, #2f54eb 0%, #85a5ff 100%)',
    '#faad14': 'linear-gradient(135deg, #D4A853 0%, #f5d89a 100%)',
};

// Fond teinte (12%) pour les pastilles de la liste compacte
const tintFor = (color, alpha = 0.12) => {
    const hex = color.replace('#', '');
    return `rgba(${parseInt(hex.slice(0, 2), 16)}, ${parseInt(hex.slice(2, 4), 16)}, ${parseInt(hex.slice(4, 6), 16)}, ${alpha})`;
};

// Affichage a deux niveaux, identique a l'accueil : 3 univers puis la liste chauffeur
const UNIVERSES = FEATURES.filter((f) => f.universe);
const DRIVER_SERVICES = FEATURES.filter((f) => !f.universe);

const FeaturesPage = () => {
    const [pageLoaded, setPageLoaded] = useState(false);

    // Animation hooks
    const [featuresRef, featuresVisible] = useScrollAnimation({ threshold: 0.1 });
    const [stepsRef, stepsVisible] = useScrollAnimation({ threshold: 0.1 });
    const [ctaRef, ctaVisible] = useScrollAnimation({ threshold: 0.1 });

    useEffect(() => {
        // Animation d'entrée de page
        setTimeout(() => setPageLoaded(true), 100);
    }, []);

    return (
        <div className="features-page">
            {/* Hero Section - Simple Banner */}
            <section className={`page-banner ${pageLoaded ? 'banner-loaded' : ''}`}>
                <div className="banner-bg-pattern"></div>
                <div className="container">
                    <h1 className={`banner-title ${pageLoaded ? 'animate-in' : ''}`}>
                        Fonctionnalités
                    </h1>
                    <p className={`banner-description ${pageLoaded ? 'animate-in' : ''}`}>
                        Découvrez tous les outils pour optimiser vos revenus
                    </p>
                    <nav className={`breadcrumb ${pageLoaded ? 'animate-in' : ''}`}>
                        <Link to="/"><HomeOutlined /> Accueil</Link>
                        <span className="separator">/</span>
                        <span className="current">Fonctionnalités</span>
                    </nav>
                </div>
            </section>

            {/* Features Grid */}
            <section
                ref={featuresRef}
                className={`features-grid-section ${featuresVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <div className={`section-header ${featuresVisible ? 'animate-fade-up is-visible' : 'animate-fade-up'}`}>
                        <span className="section-badge">Services</span>
                        <h2>Nos <span className="text-gold">Fonctionnalités</span></h2>
                        <p>Découvrez tout ce que TIGOON Connect peut faire pour vous</p>
                    </div>
                    {/* Niveau 1 : les 3 univers */}
                    <div className="universe-grid">
                        {UNIVERSES.map((universe, index) => {
                            const IconComponent = iconMap[universe.icon] || ThunderboltOutlined;

                            return (
                                <Link
                                    to={`/features/${universe.key}`}
                                    key={universe.key}
                                    className={`universe-card is-clickable ${featuresVisible ? 'animate-scale is-visible' : 'animate-scale'}`}
                                    style={{ transitionDelay: `${0.1 + index * 0.08}s` }}
                                >
                                    <div
                                        className="universe-icon"
                                        style={{ background: colorGradients[universe.color] }}
                                    >
                                        <IconComponent />
                                    </div>
                                    <h3>{universe.title}</h3>
                                    <p className="universe-description">{universe.description}</p>
                                    <ul className="universe-points">
                                        {universe.points.map((point) => (
                                            <li key={point}>
                                                <CheckCircleFilled style={{ color: universe.color }} />
                                                <span>{point}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <span className="universe-link">
                                        En savoir plus <ArrowRightOutlined />
                                    </span>
                                </Link>
                            );
                        })}
                    </div>

                    {/* Niveau 2 : les services chauffeur, en liste compacte */}
                    <div className="driver-services">
                        <div className="driver-services-title">
                            <span>Et aussi, pour les chauffeurs</span>
                        </div>
                        <div className="driver-services-grid">
                            {DRIVER_SERVICES.map((service, index) => {
                                const IconComponent = iconMap[service.icon] || ThunderboltOutlined;

                                return (
                                    <Link
                                        to={`/features/${service.key}`}
                                        key={service.key}
                                        className={`driver-service is-clickable ${featuresVisible ? 'animate-fade-up is-visible' : 'animate-fade-up'}`}
                                        style={{ transitionDelay: `${0.35 + index * 0.05}s` }}
                                    >
                                        <div
                                            className="driver-service-icon"
                                            style={{ background: tintFor(service.color), color: service.color }}
                                        >
                                            <IconComponent />
                                        </div>
                                        <div className="driver-service-text">
                                            <h4>{service.title}</h4>
                                            <p>{service.shortDescription}</p>
                                        </div>
                                        <RightOutlined className="driver-service-chevron" />
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section
                ref={stepsRef}
                className={`how-it-works ${stepsVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <div className={`section-header ${stepsVisible ? 'animate-fade-down is-visible' : 'animate-fade-down'}`}>
                        <span className="section-badge">Processus</span>
                        <h2>Comment <span className="text-gold">ça marche</span> ?</h2>
                        <p>Rejoignez TIGOON Connect en 3 étapes simples</p>
                    </div>
                    <Row gutter={[32, 48]} className="steps-grid">
                        <Col xs={24} sm={8}>
                            <div
                                className={`step ${stepsVisible ? 'animate-fade-up is-visible' : 'animate-fade-up'}`}
                                style={{ transitionDelay: '0.1s' }}
                            >
                                <div className="step-icon">
                                    <DownloadOutlined />
                                </div>
                                <div className="step-number">1</div>
                                <h3>Téléchargez</h3>
                                <p>Téléchargez l'application TIGOON Connect depuis votre store</p>
                            </div>
                        </Col>
                        <Col xs={24} sm={8}>
                            <div
                                className={`step ${stepsVisible ? 'animate-fade-up is-visible' : 'animate-fade-up'}`}
                                style={{ transitionDelay: '0.2s' }}
                            >
                                <div className="step-icon">
                                    <UserAddOutlined />
                                </div>
                                <div className="step-number">2</div>
                                <h3>Inscrivez-vous</h3>
                                <p>Créez votre compte avec vos informations de chauffeur</p>
                            </div>
                        </Col>
                        <Col xs={24} sm={8}>
                            <div
                                className={`step ${stepsVisible ? 'animate-fade-up is-visible' : 'animate-fade-up'}`}
                                style={{ transitionDelay: '0.3s' }}
                            >
                                <div className="step-icon">
                                    <CheckCircleOutlined />
                                </div>
                                <div className="step-number">3</div>
                                <h3>Profitez</h3>
                                <p>Accédez à toutes les fonctionnalités et avantages exclusifs</p>
                            </div>
                        </Col>
                    </Row>
                    {/* Connecting line */}
                    <div className={`steps-connector ${stepsVisible ? 'animate-in' : ''}`}></div>
                </div>
            </section>

            {/* CTA Section */}
            <section
                ref={ctaRef}
                className={`cta-section ${ctaVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <div className={`cta-content ${ctaVisible ? 'animate-scale is-visible' : 'animate-scale'}`}>
                        <h2>Prêt à rejoindre <span className="text-gold">TIGOON Connect</span> ?</h2>
                        <p>Téléchargez l'application maintenant et commencez à profiter de tous les avantages</p>
                        <div className="cta-buttons">
                            <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className="store-badge">
                                <img
                                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                                    alt="Google Play"
                                    height="50"
                                />
                            </a>
                            {APP_STORE_AVAILABLE ? (
                                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="store-badge">
                                    <img
                                        src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                                        alt="App Store"
                                        height="50"
                                    />
                                </a>
                            ) : (
                                <div className="store-badge-coming-soon">
                                    <AppleOutlined />
                                    <span>Bientôt disponible</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FeaturesPage;
